import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/base/Seo";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "精准导视",
    text: "以当季新番导视为核心，为读者甄别值得追的作品，省去盲目开坑的时间。",
  },
  {
    icon: "ri-flashlight-line",
    title: "每日更新",
    text: "最新上架内容每日更新，热门新番同步追，让读者始终与连载节奏保持同步。",
  },
  {
    icon: "ri-lightbulb-flash-line",
    title: "内容为先",
    text: "坚持真实、中立、有温度的内容原则，不夸大、不标题党，把好作品如实呈现给读者。",
  },
  {
    icon: "ri-heart-3-line",
    title: "读者共创",
    text: "珍视每一位读者的反馈，把大家的口碑与建议融入运营，让网站和读者一起成长。",
  },
];

const milestones = [
  { year: "起步", text: "以「本季新作抢先看」为初心，专注整理新番导视与上架情报。" },
  { year: "成长", text: "扩展热门新番与连载新开栏目，每日上架更新逐渐成型。" },
  { year: "今天", text: "成为众多读者每日追更的信息入口，持续上新、持续陪伴。" },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们 - 番木瓜漫画"
        description="了解番木瓜漫画的网站定位、发展理念与内容原则。番木瓜漫画专注于动漫新番与连载资讯，坚持每日更新与精准导视，陪伴每一位读者追更成长。"
        keywords="番木瓜漫画,番木瓜漫画关于我们,番木瓜漫画网站定位,番木瓜漫画内容原则"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于番木瓜漫画",
            description:
              "番木瓜漫画是一个专注于动漫新番与连载资讯的内容网站。",
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        {/* header */}
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="text-xs text-background-200/70">
              <Link to="/" className="transition hover:text-primary-300">
                首页
              </Link>
              <span className="mx-2">/</span>
              <span className="text-primary-300">关于我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-4xl">
              关于番木瓜漫画
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-background-100/85 md:text-base">
              番木瓜漫画是一个专注于动漫新番与连载资讯的内容网站，致力于用准确、及时的导视与上架信息，帮助每一位热爱漫画的读者快速锁定值得追的作品。
            </p>
          </div>
        </section>

        {/* brand story */}
        <section className="bg-background-50">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-8 px-4 py-12 md:grid-cols-2 md:px-6 md:py-16">
            <div className="order-2 overflow-hidden rounded-3xl md:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20reading%20space%20with%20manga%20and%20comic%20books%20on%20a%20warm%20wooden%20table%2C%20soft%20orange%20afternoon%20light%2C%20bookshelves%20with%20colorful%20comics%2C%20warm%20and%20inviting%20atmosphere%2C%20editorial%20photography%2C%20no%20people&width=1000&height=800&seq=papaya-about-01&orientation=portrait"
                alt="番木瓜漫画 追更阅读空间"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-xs font-bold uppercase tracking-widest text-accent-600">
                OUR STORY
              </p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">
                以番木瓜之名，让追更更简单
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                名字里的「番木瓜」，取意自那份饱满、鲜活与热情——正如一部好作品带给读者的感受。我们相信，找作品、追更新不该是件麻烦事。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                因此，番木瓜漫画把新番导视、最新上架、热门新番与连载新开汇聚在一处，用清晰的栏目和准时的更新，把「值得追什么」和「现在更到哪」讲得明明白白。
              </p>
              <Link
                to="/guide"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
              >
                去看看本季新番导视
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* positioning & philosophy */}
        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-background-200 bg-background-50 p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                  <i className="ri-compass-2-line text-xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-2xl text-foreground-950">
                  网站定位
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">
                  番木瓜漫画定位于「新番资讯与追更指南」，服务对象是追求效率与品质的动漫读者。我们不只搬运信息，更提供经过梳理的导视与口碑参考，帮助读者在信息洪流中快速锁定真正值得追的作品。
                </p>
              </div>
              <div className="rounded-3xl border border-background-200 bg-background-50 p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-500 text-background-50">
                  <i className="ri-leaf-line text-xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-2xl text-foreground-950">
                  发展理念
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">
                  我们相信，内容的价值在于真实与真诚。番木瓜漫画坚持「每日更新、精准导视、读者为本」，用稳定与可靠建立信任，让每一个栏目都真正服务到追更路上的读者。
                </p>
              </div>
            </div>

            {/* content principles */}
            <div className="mt-6 rounded-3xl border border-background-200 bg-background-50 p-8">
              <h2 className="font-heading text-2xl text-foreground-950">
                内容原则
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {values.map((v) => (
                  <div key={v.title} className="rounded-2xl bg-background-100 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-500 text-background-50">
                      <i className={`${v.icon} text-lg`}></i>
                    </span>
                    <h3 className="mt-3 text-base font-bold text-foreground-900">
                      {v.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">
                      {v.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* journey */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {milestones.map((m, i) => (
                <div key={m.year} className="relative rounded-2xl border border-background-200 bg-background-50 p-6">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                    {i + 1}
                  </span>
                  <h3 className="mt-3 text-base font-bold text-primary-700">
                    {m.year}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">
                    {m.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* reputation */}
        <section className="bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
              读者口碑
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              听听追更路上的读者怎么说：
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                {
                  name: "小橘",
                  role: "追番三年",
                  text: "每天刷一下「最新上架」成了习惯，新作更新真的准时不漏，导视也帮我避了好几次雷。",
                },
                {
                  name: "阿羽",
                  role: "热血漫爱好者",
                  text: "热门新番榜很靠谱，跟着榜单追了好几部口碑作，评分和推荐都比较实在，不虚高。",
                },
                {
                  name: "Momo",
                  role: "连载党",
                  text: "连载新开板块太友好了，长篇刚开坑就能发现，从此再也不用担心错过好故事的开头。",
                },
              ].map((t) => (
                <figure
                  key={t.name}
                  className="rounded-2xl border border-background-200 bg-background-100/60 p-6"
                >
                  <div className="flex items-center gap-1 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-7 text-foreground-700">
                    "{t.text}"
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-background-50">
                      {t.name[0]}
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-foreground-900">
                        {t.name}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {t.role}
                      </span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>

            {/* contact cta */}
            <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl bg-foreground-950 px-6 py-10 text-center sm:flex-row sm:justify-between sm:text-left">
              <div>
                <h3 className="font-heading text-xl text-background-50">
                  想推荐新作或给我们建议？
                </h3>
                <p className="mt-2 text-sm text-background-100/80">
                  你的每一条反馈，都是我们前进的动力。
                </p>
              </div>
              <Link
                to="/contact"
                className="flex shrink-0 items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
              >
                联系我们
                <i className="ri-mail-send-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}